// Input Prompt Builder Utility
// Creates well-structured, sectioned input prompts for LLM processing

/**
 * Builds a structured input prompt with clear sections for LLM processing
 * @param {Object} params - Parameters for building the prompt
 * @param {string} params.userQuery - The main user query/message
 * @param {string} params.quotedContent - Quoted message content (optional)
 * @param {Object} params.systemContext - System context values (optional)
 * @param {string} params.systemContext.companyCode - Company code
 * @param {string} params.systemContext.regionCode - Region code
 * @param {string} params.systemContext.userCode - User code
 * @param {string} params.systemContext.username - Username
 * @param {string} params.systemContext.currentDate - Current date stamp
 * @returns {string} Formatted input prompt with clear sections
 */
export const buildStructuredInputPrompt = ({ userQuery, quotedContent, systemContext }) => {
  const sections = [];

  // Add quoted context section if present
  if (quotedContent && quotedContent.trim()) {
    sections.push(
      '=== QUOTED CONTEXT ===',
      quotedContent.trim(),
      ''
    );
  }

  const hasSystemContext =
    systemContext &&
    (systemContext.companyCode ||
      systemContext.regionCode ||
      systemContext.userCode ||
      systemContext.username ||
      systemContext.currentDate);

  // Add system context section if any context values exist
  if (hasSystemContext) {
    sections.push('=== SYSTEM CONTEXT ===');
    
    if (systemContext.companyCode) {
      sections.push(`Company Code: ${systemContext.companyCode}`);
    }
    if (systemContext.regionCode) {
      sections.push(`Region Code: ${systemContext.regionCode}`);
    }
    if (systemContext.userCode) {
      sections.push(`User Code: ${systemContext.userCode}`);
    }
    if (systemContext.username) {
      sections.push(`Username: ${systemContext.username}`);
    }
    if (systemContext.currentDate) {
      sections.push(`Current Date: ${systemContext.currentDate}`);
    }
    
    sections.push('');
  }

  // Add user query section
  sections.push('=== USER QUERY ===');
  sections.push(userQuery.trim());

  return sections.join('\n');
};

/**
 * Extracts system context from props and localStorage
 * @param {Object} props - Component props
 * @param {string} props.companyCode - Company code from props
 * @param {string} props.regionCode - Region code from props
 * @param {string} props.userCode - User code from props
 * @param {string} props.username - Username from SSID/localStorage
 * @returns {Object} System context object
 */
export const getSystemContext = ({ companyCode, regionCode, userCode, username }) => {
  const today = new Date().toISOString().split('T')[0];

  return {
    companyCode: companyCode || localStorage.getItem('company_code') || '',
    regionCode: regionCode || localStorage.getItem('region_code') || '',
    userCode: userCode || localStorage.getItem('user_code') || '',
    username: username || localStorage.getItem('LoginUser_name') || '',
    currentDate: today,
  };
};

/**
 * Processes different message types and extracts relevant data
 * @param {string|Object} message - Message data
 * @returns {Object} Processed message data
 */
export const processMessageData = (message) => {
  if (typeof message === 'string') {
    return {
      userQuery: message,
      quotedContent: null,
      isQuoted: false
    };
  }

  if (typeof message === 'object' && message.isQuoted) {
    return {
      userQuery: message.additionalText || '',
      quotedContent: message.quotedContent || '',
      isQuoted: true
    };
  }

  if (typeof message === 'object' && !message.isQuoted) {
    return {
      userQuery: message.additionalText || '',
      quotedContent: null,
      isQuoted: false
    };
  }

  // Fallback for unexpected message types
  return {
    userQuery: String(message),
    quotedContent: null,
    isQuoted: false
  };
};

export default {
  buildStructuredInputPrompt,
  getSystemContext,
  processMessageData
};
